import { useQuery } from '@tanstack/react-query';
import { DataTableFilterMeta, DataTablePageEvent, DataTableSortEvent } from 'primereact/datatable';
import { useCallback, useMemo, useState } from 'react';

import { useAuth } from '@src/main/shared/context/AuthContext';
import { PageResponse } from '@src/main/shared/infrastructure/entity/page.entity';

import { findPositionAssignmentsByCriteria } from '@src/main/modules/reports/positionassignments/application/use-cases/find-position-assignments-by-criteria.usecase';

import { createPositionAssignmentsHttpRepository } from '@src/main/modules/reports/positionassignments/infrastructure/repository/PositionAssignmentsHttpRepository';

import {
  PositionAssignmentReport,
  PositionAssignmentRequest,
} from '@src/main/modules/reports/shared/domain/report.interface';

export function usePositionAssignmentReports(
  initialFilters: DataTableFilterMeta,
  mapFiltersToCriteria: (
    filters: DataTableFilterMeta,
    emptyCriteria: PositionAssignmentRequest,
  ) => PositionAssignmentRequest,
  emptyCriteria: PositionAssignmentRequest,
) {
  const { user } = useAuth();

  /**
   * UI filters (PrimeReact)
   */
  const [filters, setFilters] = useState<DataTableFilterMeta>(initialFilters);

  /**
   * Pagination & sorting
   */
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>('flight_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  /**
   * Auth token provider
   */
  const getToken = useCallback(async () => user?.token ?? '', [user?.token]);

  const tokenProvider = useMemo(() => ({ getToken }), [getToken]);

  /**
   * Repository
   */
  const repository = useMemo(
    () => (user?.token ? createPositionAssignmentsHttpRepository(tokenProvider) : null),
    [tokenProvider, user?.token],
  );

  /**
   * Reset pagination
   */
  const resetPage = useCallback(() => {
    setPageNumber(0);
  }, []);

  /**
   * Build backend criteria (filters → request)
   */
  const criteria = useMemo<PositionAssignmentRequest>(() => {
    const base = mapFiltersToCriteria(filters, emptyCriteria);

    return {
      ...base,
      offset: pageNumber * pageSize,
      limit: pageSize,
      sortField,
      sortOrder,
    };
  }, [filters, emptyCriteria, pageNumber, pageSize, sortField, sortOrder, mapFiltersToCriteria]);
  /**
   * Data query
   */
  const { data, isLoading, isFetching, refetch } = useQuery<PageResponse<PositionAssignmentReport>>(
    {
      queryKey: ['positionAssignmentReports', criteria],
      queryFn: async () => {
        if (!repository) {
          throw new Error('Repository not available');
        }
        return findPositionAssignmentsByCriteria(repository, criteria);
      },
      enabled: !!repository,
      placeholderData: (prev) => prev,
      staleTime: 1000 * 60,
    },
  );

  /**
   * Pagination handlers
   */
  const handlePageChange = useCallback((event: DataTablePageEvent) => {
    setPageNumber(Math.floor(event.first / event.rows));
    setPageSize(event.rows);
  }, []);

  /**
   * Sorting handler
   */
  const handleSort = useCallback((event: DataTableSortEvent) => {
    setSortField(event.sortField ?? 'flight_date');
    setSortOrder(event.sortOrder === 1 ? 'asc' : 'desc');
  }, []);

  return {
    filters,
    setFilters,

    reports: data?.items ?? [],
    total: data?.total ?? 0,

    isLoading,
    isFetching,

    pageNumber,
    pageSize,
    sortField,
    sortOrder,

    handlePageChange,
    handleSort,
    resetPage,
    refetchReports: refetch,
  };
}
